import { useState } from 'react';
import { Layers, ShoppingCart, Minus, Plus } from 'lucide-react';
import { Modal, Button, Card, Loading } from '../common';
import { useInventory, usePurchaseItem } from '../../hooks/useShop';
import { useCurrencyBalances } from '../../hooks/useShop';
import type { InventoryItem } from '../../types/inventory';

interface DeepDiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (question: string, cardCount: number) => void;
  isSubmitting?: boolean;
  onSuccess?: () => void; // Callback after successful submission
}

// Deep dive item ID 66 from seed data (031_seed_gemini_virtual_items)
const DEEP_DIVE_ITEM_ID = 66;
const DEEP_DIVE_COIN_COST = 1;

function findDeepDiveItem(inventory: InventoryItem[]): InventoryItem | undefined {
  // Match by itemId first (most reliable), then fallback to type/name
  return inventory.find(
    item =>
      item.itemId === DEEP_DIVE_ITEM_ID ||
      item.itemType === 'deep_dive' ||
      item.itemName?.toLowerCase().includes('deep dive') ||
      item.itemName?.toLowerCase().includes('deep_dive')
  );
}

export function DeepDiveModal({ isOpen, onClose, onSubmit, isSubmitting, onSuccess }: DeepDiveModalProps) {
  const [question, setQuestion] = useState('');
  const [cardCount, setCardCount] = useState(1);
  const [showPurchase, setShowPurchase] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const { data: inventoryData, isLoading: isLoadingInventory, refetch: refetchInventory } = useInventory();
  const { data: balancesData } = useCurrencyBalances();
  const purchaseItem = usePurchaseItem();

  const inventory = inventoryData?.data?.inventory ?? [];
  const deepDiveItem = findDeepDiveItem(inventory);
  const hasDeepDive = deepDiveItem ? deepDiveItem.quantity > 0 : false;

  // Coin balance (currency id 1 = coins)
  const coinBalance = balancesData?.data?.find((b: any) => b.currency?.symbol === 'COIN' || b.currencyId === 1)?.balance ?? 0;
  const canAfford = coinBalance >= DEEP_DIVE_COIN_COST;

  const handlePurchase = async () => {
    if (!deepDiveItem && !canAfford) return;
    setIsPurchasing(true);
    try {
      // Find the deep dive item in shop catalog by name — use itemId from inventory if available
      // The purchase endpoint needs the virtual_item itemId
      // We'll look it up from the inventory item metadata or use a known fallback
      const itemId = deepDiveItem?.itemId;
      if (!itemId) {
        // If not in inventory yet, we need to find it from shop — for now show error
        throw new Error('Deep dive item not found in catalog');
      }
      await purchaseItem.mutateAsync({ itemId, quantity: 1 });
      await refetchInventory();
      setShowPurchase(false);
    } catch (err) {
      console.error('Purchase failed:', err);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleSubmit = () => {
    if (!question.trim() || !hasDeepDive) return;
    onSubmit(question.trim(), cardCount);
    // Refetch inventory after submission to update remaining count
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleClose = () => {
    setQuestion('');
    setCardCount(1);
    setShowPurchase(false);
    onClose();
  };

  // Purchase sub-modal
  if (showPurchase) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Purchase Deep Dive" size="sm">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-4xl mb-3">🔮</div>
            <p className="text-gray-300 text-sm">
              Deep Dive lets you explore a reading further with additional cards and a focused question.
            </p>
          </div>

          <Card padding="sm" className="bg-dark-700 border border-dark-600">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white font-semibold">Deep Dive (×1)</p>
                <p className="text-gray-400 text-xs">Single use • Stackable up to 99</p>
              </div>
              <div className="text-right">
                <p className="text-yellow-400 font-bold">🪙 {DEEP_DIVE_COIN_COST}</p>
                <p className="text-gray-500 text-xs">Your balance: {coinBalance}</p>
              </div>
            </div>
          </Card>

          {!canAfford && (
            <p className="text-red-400 text-sm text-center">
              Not enough coins. Visit the shop to purchase more.
            </p>
          )}

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowPurchase(false)} fullWidth disabled={isPurchasing}>
              Back
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={!canAfford || isPurchasing}
              isLoading={isPurchasing}
              fullWidth
            >
              Buy for 🪙 {DEEP_DIVE_COIN_COST}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Deep Dive" size="md">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Layers className="h-5 w-5 text-primary-400" />
          </div>
          <p className="text-gray-400 text-sm">
            Explore your reading deeper with a focused follow-up question and additional cards drawn from the same energy.
          </p>
        </div>

        {/* Inventory status */}
        {isLoadingInventory ? (
          <div className="flex justify-center py-4">
            <Loading size="sm" />
          </div>
        ) : hasDeepDive ? (
          <Card padding="sm" className="bg-green-900/20 border border-green-500/30">
            <div className="flex items-center justify-between">
              <span className="text-green-400 text-sm font-medium">🔮 Deep Dive available</span>
              <span className="text-green-300 text-sm">×{deepDiveItem!.quantity} remaining</span>
            </div>
          </Card>
        ) : (
          <Card padding="sm" className="bg-yellow-900/20 border border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-400 text-sm font-medium">No Deep Dive items</p>
                <p className="text-yellow-300/70 text-xs">Purchase one to continue</p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowPurchase(true)}
                className="flex items-center gap-1"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                Buy (🪙 {DEEP_DIVE_COIN_COST})
              </Button>
            </div>
          </Card>
        )}

        {/* Question input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Your follow-up question
          </label>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="What aspect of this reading would you like to explore further?"
            rows={3}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-primary-500 resize-none"
            disabled={!hasDeepDive}
          />
          <p className="text-xs text-gray-500">{question.length}/200 characters</p>
        </div>

        {/* Card count selector */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Additional cards to draw
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setCardCount(c => Math.max(1, c - 1))}
              disabled={cardCount <= 1 || !hasDeepDive}
              className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-white disabled:opacity-40 hover:border-primary-500 transition-colors"
              aria-label="Decrease card count"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="text-2xl font-bold text-white w-8 text-center">{cardCount}</span>
            <button
              type="button"
              onClick={() => setCardCount(c => Math.min(3, c + 1))}
              disabled={cardCount >= 3 || !hasDeepDive}
              className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-white disabled:opacity-40 hover:border-primary-500 transition-colors"
              aria-label="Increase card count"
            >
              <Plus className="h-4 w-4" />
            </button>
            <span className="text-gray-400 text-sm">card{cardCount !== 1 ? 's' : ''} (1–3)</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={handleClose} fullWidth disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasDeepDive || !question.trim() || isSubmitting}
            isLoading={isSubmitting}
            fullWidth
          >
            Start Deep Dive
          </Button>
        </div>
      </div>
    </Modal>
  );
}
